import { useCallback, useEffect, useRef } from "react"

import {
  BlockchainNetwork,
  ConstantsHelper,
  PoolMetadataCoreType,
  SOLANA_PROXY_PROGRAM_ID,
  SOLEND_PROGRAM_ID,
  createObligationAddress,
  fetchObligationByAddress,
  fetchObligationsByAddress,
  fetchPoolMetadata,
  fetchTokensInfo,
  fetchWalletAssets,
  parseLendingMarket,
  parseRateLimiter,
} from "@renec-foundation/relend-adapter"
import { useAtom, useAtomValue, useSetAtom } from "jotai"

import { configAtom, solendConfigs } from "@/atoms/config"
import { metadataAtom } from "@/atoms/metadata"
import { mainRawObligationsAtom, selectedMainObligationAddressAtom } from "@/atoms/obligations"
import { poolsAtom, rateLimiterAtom, selectedMainPoolAddressAtom, selectedMainPoolAtom } from "@/atoms/pools"
import { connectionAtom, isMarketReadyAtom, networkAtom, programIdAtom, selectedRpcAtom } from "@/atoms/settings"
import {
  SetupOnChainLoadingStep,
  refreshSetupOnChainByKeyAtom,
  resetSetupOnChainRefreshKeyAtom,
  setupOnChainDataAtom,
  setupOnChainErrorAtom,
  setupOnChainLoadingStepAtom,
  shouldRefreshSetupOnChainAtom,
} from "@/atoms/setupOnChainState"
import { RawObligationType, publicKeyAtom, uniqueAssetsAtom } from "@/atoms/shared"
import { selectedSolendObligationAddressAtom, solendRawObligationsAtom } from "@/atoms/solendObligations"
import { selectedSolendPoolAddress, solendRateLimiterAtom } from "@/atoms/solendPools"
import { rawWalletDataAtom } from "@/atoms/wallet"
import { DEBUG_MODE, IS_MAINNET, getProgramId, getRpcEndpointUrl, getWSRpcEndpointUrl } from "@/utils/constants"
import { isEmpty } from "@/utils/helpers"
import { Connection, PublicKey } from "@solana/web3.js"
import { useCurrentSlot } from "./useCurrentSlot"
import useWalletConnect from "./useWalletConnect"

const METADATA_DEFAULT = {}
const RAW_OBLIGATION_DEFAULT = {}

export const INTERVAL_RELOAD_CURRENT_SLOT = 10000

const checkMarketReady = (
  config: PoolMetadataCoreType[],
  metadata: any,
  rateLimiter: ReturnType<typeof parseRateLimiter> | null,
  solendRateLimiter: ReturnType<typeof parseRateLimiter> | null,
  walletData: Awaited<ReturnType<typeof fetchWalletAssets>> | null,
  publicKey: string | undefined,
  isSolanaChain: boolean,
) => {
  // Check if all required data exists and is not empty
  const isSolend = IS_MAINNET && isSolanaChain
  const isSolendRateLimiterEmpty = isSolend && isEmpty(solendRateLimiter)
  return (
    !isEmpty(config) &&
    !isEmpty(metadata) &&
    !isEmpty(rateLimiter) &&
    (!isSolend || !isSolendRateLimiterEmpty) &&
    // walletData can be empty if no wallet is connected
    (walletData === null || !isEmpty(walletData))
  )
}

const useSetupOnChain = () => {
  // publicKey
  const { address: walletAddress, isSolanaChain } = useWalletConnect()

  // refresh
  const connection = useAtomValue(connectionAtom)
  const [selectedMainPoolAddress, setSelectedMainPoolAddress] = useAtom(selectedMainPoolAddressAtom)
  const selectedMainPool = useAtomValue(selectedMainPoolAtom)
  const pools = useAtomValue(poolsAtom)
  const [metadata, setMetadata] = useAtom(metadataAtom)
  const [config, setConfig] = useAtom(configAtom)
  const setPublicKey = useSetAtom(publicKeyAtom)
  const { currentSlot, refreshCurrentSlot } = useCurrentSlot()
  const uniqueAssets = useAtomValue(uniqueAssetsAtom)

  const setMarketReady = useSetAtom(isMarketReadyAtom)

  /**
   * Config for switching networks
   */
  const setSelectedRpc = useSetAtom(selectedRpcAtom)
  const network = useAtomValue(networkAtom)
  const [programId, setProgramId] = useAtom(programIdAtom)

  // obligations
  const [mainRawObligations, setMainRawObligations] = useAtom(mainRawObligationsAtom)
  const [solendRawObligations, setSolendRawObligations] = useAtom(solendRawObligationsAtom)
  const setSelectedObligationAddress = useSetAtom(selectedMainObligationAddressAtom)
  const setSelectedSolendObligationAddress = useSetAtom(selectedSolendObligationAddressAtom)

  // walletData
  const [rawWalletData, setRawWalletData] = useAtom(rawWalletDataAtom)

  // rateLimiter
  const [rateLimiter, setRateLimiter] = useAtom(rateLimiterAtom)
  const [solendRateLimiter, setSolendRateLimiter] = useAtom(solendRateLimiterAtom)

  const [data, setData] = useAtom(setupOnChainDataAtom)
  const [loadingStep, setLoadingStep] = useAtom(setupOnChainLoadingStepAtom)
  const [, setError] = useAtom(setupOnChainErrorAtom)
  const shouldRefreshSetupOnChain = useAtomValue(shouldRefreshSetupOnChainAtom)
  const resetSetupOnChainRefreshKey = useSetAtom(resetSetupOnChainRefreshKeyAtom)
  const refreshSetupOnChainByKey = useSetAtom(refreshSetupOnChainByKeyAtom)

  const isFetchingRef = useRef(false)

  // Add a ref to track the network that started the operation
  const operationNetworkRef = useRef(network)

  const loadObligation = useCallback(
    async (
      fullLoad: boolean,
      config: PoolMetadataCoreType[],
      walletAddress: string,
      connection: Connection,
      network: BlockchainNetwork,
      programId: string,
      setRawObligations: (obligations: { [address: string]: RawObligationType }) => void,
    ) => {
      if (!walletAddress) return

      let obligationFromAddress = walletAddress
      if (programId === SOLEND_PROGRAM_ID) {
        const [userAuthorityInfo] = PublicKey.findProgramAddressSync(
          [Buffer.from("user_authority"), new PublicKey(walletAddress).toBuffer()],
          new PublicKey(SOLANA_PROXY_PROGRAM_ID),
        )
        obligationFromAddress = userAuthorityInfo.toBase58()
      }

      const keys = await Promise.all(
        config.map((pool) => createObligationAddress(obligationFromAddress, pool.address, programId)),
      )
      const obligations = fullLoad
        ? await fetchObligationsByAddress(network, keys, connection, DEBUG_MODE)
        : keys.map((o) => ({
            pubkey: new PublicKey(o),
            info: null,
          }))

      setRawObligations(Object.fromEntries(obligations.map((obligation) => [obligation.pubkey.toBase58(), obligation])))
    },
    [],
  )

  const updateState = useCallback(
    async (step: SetupOnChainLoadingStep, newData: Partial<typeof data>) => {
      setData((prev) => ({ ...prev, ...newData }))
      setLoadingStep(step)
    },
    [setData, setLoadingStep],
  )

  useEffect(() => {
    const fetchSetupOnChainData = async () => {
      if (isFetchingRef.current || !pools) return
      isFetchingRef.current = true

      // Store the network at the start of the operation
      operationNetworkRef.current = network

      try {
        setMarketReady(false)
        const shouldRefreshCurrentStep = shouldRefreshSetupOnChain[loadingStep]

        if (!shouldRefreshCurrentStep) {
          if (loadingStep === "config" && !isEmpty(data.config)) {
            setLoadingStep("metadata")
            return
          } else if (loadingStep === "metadata" && !isEmpty(data.metadata) && !isEmpty(data.rateLimiter)) {
            setLoadingStep("walletData")
            return
          } else if (loadingStep === "walletData" && !isEmpty(data.walletData)) {
            setLoadingStep("publicKey")
            return
          } else if (loadingStep === "publicKey" && data.publicKey !== undefined) {
            setLoadingStep("complete")
            return
          }
        }

        switch (loadingStep) {
          case "config": {
            console.log("DOING CONFIG")
            await refreshCurrentSlot()
            const configResult = await fetchPoolMetadata(network, connection, true, DEBUG_MODE)

            // Check if network changed during the await operations then skipping updates
            if (operationNetworkRef.current !== network) {
              return
            }

            await updateState("metadata", { config: configResult })
            setSelectedMainPoolAddress(configResult[0]?.address)
            setConfig(configResult)
            resetSetupOnChainRefreshKey("config")
            break
          }
          case "metadata": {
            try {
              // Run all three operations in parallel
              if (uniqueAssets.length) {
                const [metadataResult, mainRateLimiterResult, solendRateLimiterResult] = await Promise.all([
                  // Metadata fetch
                  uniqueAssets.length
                    ? fetchTokensInfo(network, uniqueAssets, connection, DEBUG_MODE)
                    : Promise.resolve(METADATA_DEFAULT),

                  // RateLimiter Main Pool fetch
                  selectedMainPoolAddress
                    ? connection.getAccountInfo(new PublicKey(selectedMainPoolAddress)).then((pool) => {
                        if (pool && currentSlot) {
                          const rateLimiter = parseLendingMarket(new PublicKey(selectedMainPoolAddress), pool).info
                            .rateLimiter
                          return parseRateLimiter(rateLimiter, currentSlot)
                        }
                        return null
                      })
                    : Promise.resolve(null),

                  // RateLimiter Solend Pool fetch
                  isSolanaChain && IS_MAINNET
                    ? connection.getAccountInfo(new PublicKey(selectedSolendPoolAddress)).then((pool) => {
                        if (pool && currentSlot) {
                          const rateLimiter = parseLendingMarket(new PublicKey(selectedSolendPoolAddress), pool).info
                            .rateLimiter
                          return parseRateLimiter(rateLimiter, currentSlot)
                        }
                        return null
                      })
                    : Promise.resolve(null),
                ])

                // Check if network changed during the await operations then skipping updates
                if (operationNetworkRef.current !== network) {
                  return
                }

                // Update all states at once
                await updateState("walletData", {
                  metadata: metadataResult,
                  rateLimiter: mainRateLimiterResult,
                  solendRateLimiter: solendRateLimiterResult,
                })

                // Update individual atoms
                setMetadata(metadataResult)
                setRateLimiter(mainRateLimiterResult)
                setSolendRateLimiter(solendRateLimiterResult)
                // Reset all refresh keys
                resetSetupOnChainRefreshKey("metadata")
              } else {
                setMetadata(METADATA_DEFAULT)
              }
              break
            } catch (error) {
              console.error("Error fetching metadata, rateLimiter, and relendWallet:", error)
              throw error
            }
          }
          case "walletData": {
            if (!data.config) return

            if (!walletAddress) {
              // Handle no wallet case
              await Promise.all([
                updateState("complete", {
                  walletData: null,
                  publicKey: null,
                }),
                setRawWalletData(null),
                setPublicKey(null),
                setMainRawObligations(RAW_OBLIGATION_DEFAULT),
              ])
            } else {
              try {
                // Run wallet data and obligation operations in parallel
                const [walletData, mainObligationResults, solanaObligationResults] = await Promise.all([
                  // Fetch wallet assets
                  fetchWalletAssets(uniqueAssets, walletAddress, connection, DEBUG_MODE),

                  // Handle main obligations
                  (async () => {
                    if (!data.config) return null

                    // Load initial obligations
                    await loadObligation(
                      false,
                      data.config,
                      walletAddress,
                      connection,
                      network,
                      programId,
                      setMainRawObligations,
                    )

                    if (selectedMainPoolAddress) {
                      const newObligationAddress = await createObligationAddress(
                        walletAddress,
                        selectedMainPoolAddress,
                        programId,
                      )

                      // Fetch selected obligation
                      const obligation = await fetchObligationByAddress(
                        network,
                        newObligationAddress,
                        connection,
                        DEBUG_MODE,
                      )

                      return { newObligationAddress, obligation }
                    }
                    return null
                  })(),

                  // Handle solend obligations
                  (async () => {
                    if (!isSolanaChain || !IS_MAINNET) return null

                    // Load initial obligations
                    await loadObligation(
                      false,
                      solendConfigs,
                      walletAddress,
                      connection,
                      network,
                      SOLEND_PROGRAM_ID,
                      setSolendRawObligations,
                    )

                    const [userAuthorityInfo] = PublicKey.findProgramAddressSync(
                      [Buffer.from("user_authority"), new PublicKey(walletAddress).toBuffer()],
                      new PublicKey(SOLANA_PROXY_PROGRAM_ID),
                    )

                    const newObligationAddress = await createObligationAddress(
                      userAuthorityInfo.toBase58(),
                      selectedSolendPoolAddress,
                      SOLEND_PROGRAM_ID,
                    )

                    // Fetch selected obligation
                    const obligation = await fetchObligationByAddress(
                      network,
                      newObligationAddress,
                      connection,
                      true,
                      DEBUG_MODE,
                    )

                    return { newObligationAddress, obligation }
                  })(),
                ])

                // Check if network changed during the await operations then skipping updates
                if (operationNetworkRef.current !== network) {
                  return
                }

                // Update states
                await Promise.all([
                  updateState("complete", {
                    walletData,
                    publicKey: walletAddress,
                  }),
                  setRawWalletData(walletData),
                  setPublicKey(walletAddress),
                ])

                // Handle main obligation results if available
                if (mainObligationResults) {
                  const { obligation, newObligationAddress } = mainObligationResults

                  if (obligation) {
                    if (obligation.info.lendingMarket.toBase58() !== selectedMainPool?.address) {
                      setSelectedMainPoolAddress(obligation.info.lendingMarket.toBase58())
                    }
                    setMainRawObligations({
                      ...mainRawObligations,
                      [newObligationAddress]: { ...mainRawObligations[newObligationAddress], ...obligation },
                    })
                  }
                  setSelectedObligationAddress(newObligationAddress)

                  // Load full obligations
                  await loadObligation(
                    true,
                    data.config,
                    walletAddress,
                    connection,
                    network,
                    programId,
                    setMainRawObligations,
                  )
                }

                // Handle solend obligations result if available
                if (solanaObligationResults) {
                  const { obligation, newObligationAddress } = solanaObligationResults

                  if (obligation) {
                    setSolendRawObligations({
                      ...solendRawObligations,
                      [newObligationAddress]: { ...solendRawObligations[newObligationAddress], ...obligation },
                    })
                  }
                  setSelectedSolendObligationAddress(newObligationAddress)

                  // Load full obligations
                  await loadObligation(
                    true,
                    solendConfigs,
                    walletAddress,
                    connection,
                    network,
                    SOLEND_PROGRAM_ID,
                    setSolendRawObligations,
                  )
                }
              } catch (error) {
                console.error("Error in wallet data and public key setup:", error)
                throw error
              }
            }

            // Reset refresh keys
            resetSetupOnChainRefreshKey(["walletData", "publicKey"])
            break
          }
        }
        if (loadingStep === "complete") {
          setMarketReady(
            checkMarketReady(
              config,
              metadata,
              rateLimiter,
              solendRateLimiter,
              rawWalletData,
              walletAddress,
              isSolanaChain,
            ),
          )
        }

        isFetchingRef.current = false
      } catch (error) {
        console.error("Error in setup on chain data fetch:", error)
        setError(error as Error)
        setLoadingStep("config")
      } finally {
        isFetchingRef.current = false
      }
    }

    fetchSetupOnChainData()
  }, [
    connection,
    currentSlot,
    rateLimiter,
    solendRateLimiter,
    rawWalletData,
    loadObligation,
    loadingStep,
    network,
    programId,
    mainRawObligations,
    refreshCurrentSlot,
    resetSetupOnChainRefreshKey,
    selectedMainPoolAddress,
    setConfig,
    setData,
    setError,
    setLoadingStep,
    setMarketReady,
    setMetadata,
    setPublicKey,
    setRateLimiter,
    setMainRawObligations,
    setRawWalletData,
    setSelectedObligationAddress,
    setSelectedMainPoolAddress,
    shouldRefreshSetupOnChain,
    uniqueAssets,
    updateState,
    walletAddress,
    isSolanaChain,
    setSolendRateLimiter,
    setSolendRawObligations,
    setSelectedSolendObligationAddress,
    solendRawObligations,
    config,
    metadata,
    data.config,
    data.metadata,
    data.rateLimiter,
    data.walletData,
    data.publicKey,
    pools,
    selectedMainPool?.address,
  ])

  // Reset loading step when wallet changes
  useEffect(() => {
    setLoadingStep("config")
  }, [walletAddress, setLoadingStep])

  // Modify network change effect
  useEffect(() => {
    if (ConstantsHelper.isRenecChain(network)) {
      setSelectedRpc({
        name: "RENEC RPC",
        endpoint: getRpcEndpointUrl() as string,
        wsEndpoint: getWSRpcEndpointUrl() as string,
      })
    } else {
      setSelectedRpc({
        name: "SOLANA RPC",
        endpoint: getRpcEndpointUrl() as string,
        wsEndpoint: getWSRpcEndpointUrl() as string,
      })
    }
    setProgramId(getProgramId(network))

    // Force reset the fetching state when network changes
    isFetchingRef.current = false

    // Update the operation network ref
    operationNetworkRef.current = network

    setLoadingStep("config")

    // Force refresh all steps and ensure config is marked for refresh
    refreshSetupOnChainByKey(["config", "metadata", "walletData", "publicKey"])
  }, [setSelectedRpc, network, setProgramId, setLoadingStep, refreshSetupOnChainByKey])

  // Add new effect for wallet changes
  useEffect(() => {
    // Only refresh wallet-dependent steps when wallet changes
    setLoadingStep("config")
    refreshSetupOnChainByKey(["walletData", "publicKey"])
  }, [walletAddress, setLoadingStep, refreshSetupOnChainByKey])

  return {
    isLoading: loadingStep !== "complete",
    currentStep: loadingStep,
    data,
    error: useAtom(setupOnChainErrorAtom)[0],
  }
}

export default useSetupOnChain
